import type { HTMLAttributes } from "react";
import { forwardRef } from "react";
import { cn } from "@/shared/utils/cn";

// Tile/card `Link`: `shadow-sm` overrides the base focus ring, so it carries the Button one.
export const TILE_LINK_CLASS_NAME =
  "rounded-lg border bg-background p-4 no-underline shadow-sm hover:border-clm-brand-blue hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function Card(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn("rounded-lg border bg-card text-card-foreground shadow-sm", className)}
      {...props}
    />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />;
  },
);

export type CardTitleProps = HTMLAttributes<HTMLHeadingElement> & {
  /**
   * Heading level for the title. Defaults to h3 (existing usage); set the
   * level that fits the surrounding document outline.
   */
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
};

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(function CardTitle(
  { className, as: Heading = "h3", ...props },
  ref,
) {
  return (
    <Heading
      ref={ref}
      className={cn(
        "text-xl font-semibold leading-none tracking-tight text-clm-primary-blue",
        className,
      )}
      {...props}
    />
  );
});

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />;
});

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />;
  },
);

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />;
  },
);
